import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useNotify } from "ra-core";
import { ExternalLink, Rocket, TriangleAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import type { CrmDataProvider } from "../providers/types";
import type {
  TestEchoLaunch,
  TestEchoLaunchStatus,
  TestEchoPack,
} from "../types";
import {
  isTestEchoInFlight,
  testEchoRefetchInterval,
  testEchoRefusalMessage,
  testEchoSenderChoices,
  testEchoStatusLabel,
  testEchoStatusVariant,
} from "./testEchoMessages";

// The operator's own WhatsApp number is not derivable from the CRM: a Supabase
// `sales` row has no WhatsApp binding. So the number is typed once and
// remembered per browser — obvious, and never guessed wrong.
const USER_ID_STORAGE_KEY = "echoflicks.testEcho.userId";

type TrackedLaunch = {
  workload_id: string;
  user_id: string;
  sender_key: string | null;
  pack_id: string;
  label: string | null;
  cost_hint_usd: string | null;
  within_24h: boolean | null;
  remaining_today: number | null;
  status: string;
};

/**
 * Labs → "Test Echo launcher" (plan 2026-09-02-test-echo-autoqueue §3.5-C /
 * TEQ-012). Queues a synthetic-but-real Echo for an admin WhatsApp number from a
 * stored image pack: the bot replays the whole production /create flow, so the
 * Echo is indistinguishable from a manual one except for its 🧪 badge.
 *
 * Every request goes through /api/test-echoes (src/server/testEchoProxy.ts),
 * which requires a CRM administrator session and injects the bot's internal key
 * server-side. This card is rendered behind the same `configuration:edit` gate
 * as the rest of Labs; it invents no permission of its own.
 */
export const TestEchoLauncherCard = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();

  const [userId, setUserId] = useState(() => readStoredUserId());
  const [label, setLabel] = useState("");
  const [watchInterview, setWatchInterview] = useState(true);
  const [clearFeedbackGate, setClearFeedbackGate] = useState(false);
  const [launches, setLaunches] = useState<TrackedLaunch[]>([]);
  const [busy, setBusy] = useState(false);
  // Only asked for once the bot says it cannot tell which sender carries this
  // number's conversation. Most environments run one, so the picker stays out
  // of the way until it is genuinely needed.
  const [senderChoices, setSenderChoices] = useState<string[]>([]);
  const [senderKey, setSenderKey] = useState("");

  const trimmedUserId = userId.trim();

  const packsQuery = useQuery({
    queryKey: ["test-echo-packs", trimmedUserId],
    queryFn: () => dataProvider.listTestEchoPacks(trimmedUserId),
    enabled: trimmedUserId.length > 0,
    retry: false,
    staleTime: 60_000,
  });

  // Rows own their own polling; they report the status back so the parent can
  // keep Launch disabled while this user has an Echo in flight. Returning the
  // same array when nothing changed keeps a poll from re-rendering the card.
  const onStatus = useCallback((workloadId: string, status: string) => {
    setLaunches((current) =>
      current.some(
        (launch) =>
          launch.workload_id === workloadId && launch.status !== status,
      )
        ? current.map((launch) =>
            launch.workload_id === workloadId ? { ...launch, status } : launch,
          )
        : current,
    );
  }, []);

  const hasInFlightForUser = launches.some(
    (launch) =>
      launch.user_id === trimmedUserId && isTestEchoInFlight(launch.status),
  );

  const onLaunch = async (pack: TestEchoPack) => {
    setBusy(true);
    try {
      const launch = await dataProvider.launchTestEcho({
        user_id: trimmedUserId,
        pack_id: pack.pack_id,
        notify: watchInterview ? "full" : "final_only",
        ...(label.trim() ? { label: label.trim() } : {}),
        ...(clearFeedbackGate ? { force_clear_feedback_gate: true } : {}),
        ...(senderKey ? { sender_key: senderKey } : {}),
      });
      setLaunches((current) => [
        trackLaunch(
          launch,
          trimmedUserId,
          label.trim() || null,
          senderKey || null,
        ),
        ...current,
      ]);
      notify(
        "Queued. The bot is walking the real /create flow — the Echo appears in Echoes shortly.",
        { type: "info" },
      );
    } catch (error) {
      const choices = testEchoSenderChoices(error);
      if (choices.length) {
        setSenderChoices(choices);
        setSenderKey((current) => current || choices[0]);
      }
      notify(testEchoRefusalMessage(error), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const packs = packsQuery.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Rocket className="size-4" />
          Test Echo launcher
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Queue a real Echo for an admin WhatsApp number from a stored image
          pack. It runs the whole production pipeline (and costs what a real
          Echo costs), and is marked 🧪 Test Echo everywhere in the CRM.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="test-echo-user-id">Admin WhatsApp number</Label>
          <Input
            id="test-echo-user-id"
            value={userId}
            placeholder="34636115993"
            inputMode="numeric"
            autoComplete="off"
            onChange={(event) => {
              setUserId(event.target.value);
              rememberUserId(event.target.value);
            }}
          />
          <p className="text-xs text-muted-foreground">
            Digits only, no “+”. The bot refuses any number that is not in its
            admin cohort.
          </p>
        </div>

        {senderChoices.length > 0 && (
          <div className="space-y-1.5">
            <Label htmlFor="test-echo-sender">WhatsApp sender</Label>
            <select
              id="test-echo-sender"
              className="border-input bg-background flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs"
              value={senderKey}
              onChange={(event) => setSenderKey(event.target.value)}
            >
              {senderChoices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              This number talks to more than one of our senders, so the bot
              cannot tell which conversation to launch in. Pick the one it
              actually uses.
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="test-echo-label">Label (optional)</Label>
          <Input
            id="test-echo-label"
            value={label}
            placeholder="ffr sheet refs run 3"
            maxLength={120}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <Label
            htmlFor="test-echo-notify"
            className="text-sm font-normal leading-snug"
          >
            Watch the interview on WhatsApp
            <span className="block text-xs text-muted-foreground">
              Off = quiet mode: only the finished video and poster are sent.
            </span>
          </Label>
          <Switch
            id="test-echo-notify"
            checked={watchInterview}
            onCheckedChange={setWatchInterview}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <Label
            htmlFor="test-echo-clear-gate"
            className="text-sm font-normal leading-snug"
          >
            Clear the rating gate
            <span className="block text-xs text-muted-foreground">
              Skips an unrated previous Echo instead of refusing with “waiting
              for feedback”.
            </span>
          </Label>
          <Switch
            id="test-echo-clear-gate"
            checked={clearFeedbackGate}
            onCheckedChange={setClearFeedbackGate}
          />
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">Packs</h3>
          {!trimmedUserId ? (
            <p className="text-sm text-muted-foreground">
              Enter an admin WhatsApp number to list its packs.
            </p>
          ) : packsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading packs…</p>
          ) : packsQuery.isError ? (
            <p className="flex items-start gap-1.5 text-sm text-destructive">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              {testEchoRefusalMessage(packsQuery.error)}
            </p>
          ) : packs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No packs for that number yet. Create one with{" "}
              <code>tools/test_echo_pack.py create --from-echo …</code>.
            </p>
          ) : (
            <ul className="space-y-2">
              {packs.map((pack) => (
                <li
                  key={pack.pack_id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {pack.name || pack.pack_id}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {packSummary(pack)}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => onLaunch(pack)}
                    disabled={busy || hasInFlightForUser}
                  >
                    <Rocket className="size-4" />
                    {hasInFlightForUser ? "Echo in flight" : "Launch"}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {launches.length > 0 ? (
          <div className="space-y-2">
            <h3 className="text-sm font-medium">This session’s launches</h3>
            {launches.map((launch) => (
              <TestEchoLaunchRow
                key={launch.workload_id}
                launch={launch}
                onStatus={onStatus}
              />
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const TestEchoLaunchRow = ({
  launch,
  onStatus,
}: {
  launch: TrackedLaunch;
  onStatus: (workloadId: string, status: string) => void;
}) => {
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { data } = useQuery({
    queryKey: ["test-echo-launch", launch.workload_id, launch.user_id],
    queryFn: () =>
      dataProvider.getTestEchoLaunch(
        launch.workload_id,
        launch.user_id,
        launch.sender_key ?? undefined,
      ),
    retry: false,
    // Poll ONLY while this launch is still moving; a delivered or failed launch
    // never changes again, so the card stops asking.
    refetchInterval: (query) =>
      testEchoRefetchInterval(
        (query.state.data as TestEchoLaunchStatus | undefined)?.status ??
          launch.status,
      ),
  });

  const status = data?.status ?? launch.status;

  useEffect(() => {
    onStatus(launch.workload_id, String(status));
  }, [launch.workload_id, onStatus, status]);

  return (
    <div className="space-y-1.5 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={testEchoStatusVariant(status)}>
          {testEchoStatusLabel(status)}
        </Badge>
        <span className="text-sm">{launch.pack_id}</span>
        {launch.label ? (
          <span className="text-xs text-muted-foreground">{launch.label}</span>
        ) : null}
        <Button asChild variant="outline" size="sm" className="ml-auto">
          <Link
            to={`/echoflicks_video_sessions/${encodeURIComponent(
              launch.workload_id,
            )}/show`}
          >
            Open Echo
            <ExternalLink className="size-4" />
          </Link>
        </Button>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="font-mono">{launch.workload_id}</span>
        {data?.step ? <span>Step: {data.step}</span> : null}
        {data?.answers_sent != null ? (
          <span>{data.answers_sent} answers sent</span>
        ) : null}
        {launch.cost_hint_usd ? <span>~${launch.cost_hint_usd}</span> : null}
        {launch.remaining_today != null ? (
          <span>{launch.remaining_today} left today</span>
        ) : null}
      </div>
      {launch.within_24h === false ? (
        <p className="flex items-start gap-1.5 text-xs text-amber-700">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          That number is outside WhatsApp&apos;s 24h window, so prompts and the
          final video cannot be delivered. Message the bot from that phone, or
          watch the Echo here instead.
        </p>
      ) : null}
    </div>
  );
};

function packSummary(pack: TestEchoPack) {
  const bits: string[] = [];
  const occasion = pack.occasion_key || pack.occasion_callback_id;
  if (occasion) bits.push(String(occasion));
  const media = pack.media_count ?? null;
  if (media != null) bits.push(`${media} media`);
  if (pack.answers_count != null) {
    bits.push(`${pack.answers_count} scripted answers`);
  }
  return bits.join(" · ") || pack.pack_id;
}

function trackLaunch(
  launch: TestEchoLaunch,
  userId: string,
  label: string | null,
  senderKey: string | null,
): TrackedLaunch {
  return {
    workload_id: launch.workload_id,
    user_id: launch.user_id || userId,
    // Remembered per launch: the status read is scoped by the same sender, so
    // polling a launch made on one sender must not read the other's state.
    sender_key: senderKey,
    pack_id: launch.pack_id || "",
    label,
    cost_hint_usd: launch.cost_hint_usd ?? null,
    within_24h: launch.whatsapp_window?.within_24h ?? null,
    remaining_today: launch.policy?.remaining_today ?? null,
    status: launch.status || "interviewing",
  };
}

function readStoredUserId() {
  try {
    return window.localStorage.getItem(USER_ID_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function rememberUserId(value: string) {
  try {
    window.localStorage.setItem(USER_ID_STORAGE_KEY, value.trim());
  } catch {
    // A browser with storage disabled just forgets the number between visits.
  }
}
